/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The kind is derived and reported, never chosen** — `X24`, `XS3` slice 2 (#1222).
 *
 * A small mono read-out sits at the head of the value field and states what the value currently is:
 * `literal`, `path`, `macro → path`, `expression`, `shared`. It is computed from **what was typed**.
 *
 * This is what closes `F5`, and the reason is worth keeping because it is not the obvious one. The
 * collision was never really that two words swapped — *Calculated* on screen being `computed`
 * underneath while `calculated` underneath was *Dynamic* on screen. It was that a **picker existed
 * at all**, which forced a five-label operator vocabulary to be kept in step with five model names
 * by hand. A read-out has one word per thing by construction, so `X8` holds without a renaming
 * exercise and the model names need not change to satisfy it.
 *
 * Kept as a pure module rather than living in the component because it is the half worth asserting:
 * the mapping from *what an operator typed* to *what gets stored* is the whole of slice 2, and a
 * component test would be asserting a rendering rather than the rule.
 */

import { canonicalizeContextPath, isContextRoot } from '@context-roots';
import { MACRO_VOCABULARY } from '../inputs/data-model-schema';
import { macroTokenName } from './mapping-value-round-trip';
import { entryVersion, type LibraryVersion } from './shared-expression-version';
import type { MappingLeaf } from './ortb-body-editor.adapters';
import {
  defaultCalculatedExpression,
  defaultComputedExpression,
  defaultConditionalExpression,
  defaultStaticExpression,
  defaultSuppressExpression,
  openingOperands,
  readComputedOperands,
  type CalculatedExpression,
  type ComputedExpression,
  type MacroExpression,
  type ReferenceExpression,
  type StaticExpression,
  type SuppressExpression,
  type ValueOperand,
  type ComputedOperand,
} from '../../../types/decorator-expression';
import { operandFromLine } from './collection-operand';

/** What the value currently is. `empty` is the honest answer for an unauthored row. */
export type ValueKind = 'empty' | 'literal' | 'path' | 'macro' | 'expression' | 'shared';

export interface DerivedValue {
  kind: ValueKind;
  /** The read-out, in the operator's words. `macro → path` when the vocabulary resolves one. */
  label: string;
  /** For a macro whose concept declares a path: the path it reaches. */
  reaches?: string;
  /** The canonical form of what was typed, where canonicalising changes it (`#1213`). */
  canonical?: string;
}

/**
 * **Does this read as a context path?**
 *
 * The test is the one the resolver actually applies: canonicalise the input — which rewrites an
 * alias spelling such as `user.deviceType` into `session.user.deviceType` (`#1213`) — and then ask
 * whether the first segment is a declared root. It is deliberately **not** *"contains a dot"*: a
 * literal like `1.5` or `roku.tv` has a dot and is not a path, and treating it as one would store a
 * `calculated` expression that resolves to nothing on every request.
 *
 * A bare root on its own (`session`) is **not** a path either — it addresses an object, not a value,
 * so reporting it as a path would claim a mapping that cannot resolve to a scalar.
 */
export function readsAsPath(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;
  const canonical = canonicalizeContextPath(trimmed);
  const [root, ...rest] = canonical.split('.');
  return rest.length > 0 && isContextRoot(root);
}

/**
 * **Whether this surface accepts a `{macro}` at all** (`XS3` slice 3).
 *
 * It was `MappingSurfaceCapabilities.tabs` not containing `'macro'` — inbound response mapping's
 * answer, and the only thing the `tabs` list ever really decided once the strip was removed. It has
 * to reach the classifier rather than only the offer list: with the tab gone, `{ip}` typed on a
 * response mapping would classify as a macro, store as one, and be silently discarded by
 * `leafToSource`, which has no macro branch. A refused capability that goes quiet is the silence
 * pattern 24 exists to end.
 */
export interface ValueKindOptions {
  /** `false` on an inbound surface: a `{macro}` is a value **bidBrain** has, not one the exchange sent. */
  acceptsMacro: boolean;
  /**
   * `false` where the destination takes only a fixed value (`acceptsDerived` refused, `#1580`): what
   * is typed is the literal it will be stored as, never a path. Absent is `true`.
   */
  acceptsPath?: boolean;
}

const DEFAULT_OPTIONS: ValueKindOptions = { acceptsMacro: true };

/**
 * What the operator typed, classified. **Text in, kind out** — nothing about the current leaf is
 * consulted, because the whole point is that the kind follows the typing rather than a stored mode.
 */
export function deriveValueKind(text: string, options: ValueKindOptions = DEFAULT_OPTIONS): DerivedValue {
  const trimmed = text.trim();
  if (!trimmed) return { kind: 'empty', label: '' };

  const token = options.acceptsMacro ? macroTokenName(trimmed) : undefined;
  if (token) {
    // `X27`: a macro is sugar over a path, so the read-out says which path it reaches when the
    // catalog knows one. Where it does not, `macro` alone is the honest answer — claiming a path
    // we cannot name is the resolution-mark defect (*a value reads as proof*).
    const reaches = MACRO_VOCABULARY.get(token)?.path;
    return reaches ? { kind: 'macro', label: 'macro → path', reaches } : { kind: 'macro', label: 'macro' };
  }

  if (options.acceptsPath !== false && readsAsPath(trimmed)) {
    const canonical = canonicalizeContextPath(trimmed);
    return { kind: 'path', label: 'path', canonical: canonical === trimmed ? undefined : canonical };
  }

  return { kind: 'literal', label: 'literal' };
}

/**
 * **Does this value render on the value line, or does it need its own editor?** — `X23`'s
 * `isSimplePath`, with the job it was given: *"it decides how the row renders, never where the row
 * goes."*
 *
 * The predicate is not about paths any more, which is why it does not keep the name. Three things
 * fit in one text field — a scalar literal, a `{macro}`, and a `calculated` expression, whose parts
 * beyond the path are the chip row's — and two do not:
 *
 * - a **structured** static (an object or a scalar array) has no single-field representation, so it
 *   gets the JSON or comma-separated editor it has always had;
 * - a **conditional**, **computed** or **suppress** expression is a shape the value line cannot
 *   show, so the field would render empty and the read-out would say `—` about a fully authored
 *   value. That is `F3`'s defect — *the model is already right, the disclosure is missing* —
 *   reappearing in the control built to close it.
 *
 * **A `static` *expression* renders on the line** (`X38`). It reads as a literal and the line holds
 * literals; the leaf just spells it as an expression rather than as a `staticValue`, which is what
 * clearing an errand on a literal produces (`simplestShapeFor` → `reshapeExpression(…, 'static')`).
 * Answering `false` for it opened the expression editor to draw a second box over the literal
 * already on the line — `X38`'s duplication, arriving through a shape rather than through a form.
 *
 * Where it is false the value line renders a **read-only summary** and the expression editor opens
 * beneath it. Nothing is rewritten either way: this decides a rendering, and `X26` is that a
 * projection may change what is shown, never what is stored.
 */
export function rendersOnValueLine(leaf: MappingLeaf): boolean {
  if (leaf.kind === 'static') return !Array.isArray(leaf.staticValue) && !isPlainObject(leaf.staticValue);
  if (leaf.kind === 'expression')
    return (
      !leaf.expression ||
      leaf.expression.type === 'calculated' ||
      leaf.expression.type === 'static' ||
      // A `{macro}` is typed on the line and read back from it (`X27`); storing it as a node (`X58`)
      // does not make it a structured shape.
      leaf.expression.type === 'macro'
    );
  return true;
}

const isPlainObject = (value: unknown): boolean => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * **The path an in-place capability composes onto, or `null` where there is none** (`XS3` slice 3).
 *
 * `CapabilityChipRow` writes a `CalculatedExpression`, which is *a path plus its parts*. Until this
 * slice it built one as `{ type: 'calculated', path: calc?.path ?? '' }` — so ticking a transform on
 * a `{macro}`, or on a literal (the chips render on both), **replaced the operator's value with an
 * empty path**. It type-checked, it stored the transform, and the value was gone; a component test
 * asserting the transform round-trips passes against it, which is why this is a function with its
 * own assertions rather than an expression inside the render.
 *
 * A macro resolves to the path its concept declares, which is `X27`'s model — *a macro is sugar over
 * a path* — so composing onto `{app_name}` produces the transform on `session.app.name`. The value
 * field then reads `path` rather than `macro → path`, which is a **visible** consequence of a real
 * edit rather than a silent one; a macro the vocabulary declares no path for has nothing to compose
 * onto at all, and the chips say so instead of guessing.
 */
export function compositionBasePath(leaf: MappingLeaf): string | null {
  if (leaf.kind !== 'expression') return null;
  if (leaf.expression?.type === 'calculated') return leaf.expression.path || null;
  if (leaf.expression?.type === 'macro') return macroPath(leaf.expression.name);
  return null;
}

/** The path a macro's concept declares — `X27`'s *a macro is sugar over a path*. `null` where none. */
function macroPath(name: string): string | null {
  const token = macroTokenName(`{${name}}`);
  return (token && MACRO_VOCABULARY.get(token)?.path) || null;
}

/* ------------------------------------------------------------------ *
 * Carrying the value into the expression editor — `X28`, `XS3` slice 4
 * ------------------------------------------------------------------ */

/**
 * **The handoff carries the value; it does not start over** (`X28`, `XS3` slice 4).
 *
 * `If…` and `Calculate…` are *errands*, named for an outcome the operator wants before they know
 * what an expression is. An errand that opens an empty editor is not the errand: the operator typed
 * `session.content.contentLength`, asked for *send one value when a condition holds*, and the
 * obvious reading is that **this** value is the one being made conditional.
 *
 * Until slice 4 the three handoff chips only revealed the editor, so the typed value was there in the
 * leaf and nowhere in the shape the editor put on screen — the operator retyped it into the branch.
 *
 * A branch is `StaticExpression | CalculatedExpression | SuppressExpression` (#542 is the widening),
 * which is exactly enough for the two things the value line can hold: a path and a literal.
 */
export type BranchExpression = StaticExpression | CalculatedExpression | SuppressExpression;

/**
 * The current value as a conditional's branch, or `null` where the value cannot be represented as one.
 *
 * `null` is a **macro the vocabulary declares no path for** — `cb`, `schain` and the other produced
 * concepts. Carrying it as a `static` `{cb}` would be wrong on more than one surface (an ORTB body
 * key that is entirely an unresolved token is dropped, and `cb` only resolves through the name-based
 * rescue on a row actually called `cb`), and carrying nothing would lose the operator's value
 * silently. So the chip is **refused with a reason** instead, which is `refusesCarry`.
 *
 * An **empty** row is not that case: there is nothing to lose, so it carries as an empty branch and
 * the errand proceeds.
 */
export function carryIntoBranch(leaf: MappingLeaf): BranchExpression | null {
  const literal = carryableLiteral(leaf);
  if (literal !== null) return { type: 'static', value: literal };
  const path = carryablePath(leaf);
  return path === null ? null : { type: 'calculated', path };
}

/**
 * The current value as an operand, on the same terms as {@link carryIntoBranch}.
 *
 * Since `E3` an operand **is** a value, so the two carriers produce the same two shapes and differ
 * only in what they are handed to. The pre-`E3` `literal`/`path` spellings are read and never
 * written, so a carry writes a `static` or a `calculated` like everything else does.
 */
export function carryIntoOperand(leaf: MappingLeaf): ComputedOperand | null {
  const literal = carryableLiteral(leaf);
  if (literal !== null) return { type: 'static', value: literal };
  const path = carryablePath(leaf);
  // `[*]` is each member of a list once it is an operand (`CO8`, `2e`) — the finding on a row that
  // holds one sends the operator to Calculate, so Calculate has to land it as one, or the redirect
  // leads to the same finding one level down.
  return path === null ? null : operandFromLine({ type: 'calculated', path });
}

/**
 * **Can the errand carry this value at all** — the one predicate both carriers agree with, so a
 * refused chip and a lost value cannot come apart.
 */
export function isCarryable(leaf: MappingLeaf): boolean {
  return carryIntoBranch(leaf) !== null;
}

/** A scalar static, as text. `null` when the value is not a literal at all. */
function carryableLiteral(leaf: MappingLeaf): string | null {
  if (leaf.kind !== 'static') return null;
  const v = leaf.staticValue;
  if (v === undefined || v === null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  // A structured static has no branch form; it is not carried and the chip says so.
  return null;
}

/**
 * The path this value reads, for a carry. Wider than {@link compositionBasePath} in exactly one way:
 * an **empty** path is carryable (there is nothing to lose) where it is not composable (a regex has
 * nothing to act on).
 */
function carryablePath(leaf: MappingLeaf): string | null {
  if (leaf.kind === 'expression') {
    const expr = leaf.expression;
    if (!expr) return '';
    if (expr.type === 'macro') return expr.name.trim() ? compositionBasePath(leaf) : '';
    return expr.type === 'calculated' ? expr.path : null;
  }
  return null;
}

/**
 * **The value, wrapped in the errand the operator asked for.** `null` where it cannot be carried.
 *
 * An expression that is **already** that shape is returned untouched — clicking `If…` on a
 * conditional is *show me it*, not *wrap it again*, and `X26` is that a view change never rewrites
 * the leaf.
 */
export function wrapForErrand(leaf: MappingLeaf, errand: 'if' | 'calculate'): MacroExpression | null {
  const current = leaf.kind === 'expression' ? leaf.expression : undefined;

  if (errand === 'if') {
    if (current?.type === 'conditional') return current;
    const then = carryIntoBranch(leaf);
    if (!then) return null;
    // `defaultConditionalExpression`'s own blank predicate — one `all` group holding one blank
    // condition, never a bare `condition` node, which is what `XS1` settled: collapsing to a bare
    // node would discard a chosen `any` the moment the operator deletes back down to one.
    return {
      ...defaultConditionalExpression(),
      then,
      ...(current?.description ? { description: current.description } : {}),
    };
  }

  if (current?.type === 'computed') return current;
  const operand = carryIntoOperand(leaf);
  if (!operand) return null;
  return {
    type: 'computed',
    operands: [operand],
    operation: 'concat',
    ...(current?.type === 'calculated' && expressionDefaultOf(current) !== undefined
      ? { default: expressionDefaultOf(current) }
      : {}),
    ...(current?.description ? { description: current.description } : {}),
  };
}

/** The default in force on a node, reading the pre-`#1216` spelling too (`X3`). */
const expressionDefaultOf = (node: { default?: string; fallback?: string }): string | undefined =>
  node.default !== undefined ? node.default : node.fallback;

/* ------------------------------------------------------------------ *
 * Changing an expression's shape without losing its value
 * ------------------------------------------------------------------ */

/**
 * **A shape change carries what both shapes can hold** — `XS3` slice 4, and it is `#1223`'s defect
 * one level down.
 *
 * `#1223` was *switching mapping tabs discards the authored value*, and slice 3 dissolved it by
 * removing the tabs. The expression editor's own **type** picker had the identical shape and survived:
 * every arm called `default…Expression()`, so moving a calculation back to a path threw the path away.
 * Rehearsing slice 4 is what surfaced it, because the errands make the round trip a normal act —
 * *take `coppa` from a path to a condition and back* is the slice's own sentence, and *back* lost the
 * value.
 *
 * **It lives here rather than in the editor because the picker is on its way out.** `X39` is that the
 * five types are shapes the control renders, never types an author picks — so the control that reads
 * this will be replaced, and the *rule about what survives a reshape* has to outlive it. The turn-2
 * editor needs exactly this function to render one shape as another.
 *
 * What carries, and what cannot:
 *
 * | | |
 * | --- | --- |
 * | **`description`** | always. A note is about the mapping, not about the shape it currently has |
 * | **`asType`** | always, except onto a `suppress`, which keeps it on what it replaced. What an expression yields is not a property of its shape (`4e`) |
 * | **`default`** | between the two shapes that declare one (`calculated`, `computed`) |
 * | **a path** | into a branch, an operand, or back out of either where it is the only one |
 * | **a literal** | the same way |
 * | **a condition, or a second operand** | **nothing.** A path has no room for either, so the switch is a real loss and the operator asked for it |
 * | **everything, through a suppression** | `suppress` transports what it replaced, so *never send it* is reversible on a branch as it already was on a row (`#1274`) |
 */
export function reshapeExpression(current: MacroExpression, next: MacroExpression['type']): MacroExpression {
  /**
   * **Leaving a suppression gives back what it replaced** (`#1274`), and the rest of this function
   * then reshapes *that* rather than the empty shape standing in for it.
   *
   * Done here rather than as an arm in `pathWithin` and its two siblings, because those carry a
   * **path** or a **literal** — all a branch could hold before `#542` — and a suppressed branch may
   * now be standing over a whole calculation. Restoring the expression itself is the only form that
   * does not quietly narrow what comes back.
   */
  const from = current.type === 'suppress' && current.replaced ? current.replaced : current;
  if (from.type === next) return from;

  const description = current.description ?? from.description;
  // **What the expression yields survives too** (`4e`) — it is about the expression's output, not
  // about the shape producing it, which is the note's argument. A suppression keeps it on what it
  // replaced rather than on itself, because a suppression yields nothing.
  const asType = current.asType ?? from.asType;
  const withNote = <T extends MacroExpression>(expr: T): T => ({
    ...expr,
    ...(description ? { description } : {}),
    ...(asType && expr.type !== 'suppress' ? { asType } : {}),
  });

  const path = pathWithin(from);
  const literal = literalWithin(from);

  switch (next) {
    case 'static':
      // A path is not a literal — writing `session.user.ip` into a fixed value would send the text.
      return withNote(defaultStaticExpression(literal ?? ''));
    case 'calculated':
      return withNote({
        ...defaultCalculatedExpression(path ?? ''),
        ...(defaultWithin(from) !== undefined ? { default: defaultWithin(from) } : {}),
      });
    case 'computed': {
      const operand: ComputedOperand | undefined =
        path !== undefined
          ? operandFromLine({ type: 'calculated', path })
          : literal !== undefined
            ? { type: 'static', value: literal }
            : undefined;
      const base = defaultComputedExpression();
      /* Opened to what the operation reads (`X53`): the value that was here, plus the empty one it
         is waiting to be combined with. `openingOperands` owns the count so the chip and the
         operation picker cannot disagree about it. */
      return withNote({
        ...base,
        operands: operand ? openingOperands(base.operation, [operand]) : base.operands,
        ...(defaultWithin(from) !== undefined ? { default: defaultWithin(from) } : {}),
      });
    }
    case 'conditional': {
      /**
       * **The default travels with the path, into the branch that holds it** (`#1241`, ratified by
       * Todd 2026-09-03: *"the other option would be to carry it on the else… either that or then is
       * fine"*).
       *
       * Both were offered and they are **not** equivalent, which is why this picks rather than
       * shrugging. A branch's `else` starts as a `static`, and a `StaticExpression` has no default
       * slot — so *carry it on the else* can only mean making it the **else's value**. That reads
       * well and it silently drops the fallback in exactly the case it existed for: condition holds,
       * path resolves to nothing, and where the original sent `0.0.0.0` the reshaped one sends
       * nothing.
       *
       * On `then` the path keeps its own fallback, and the empty `else` is a branch the operator has
       * just created and has to answer anyway (`X41`: *`Otherwise` is always present and always
       * answered*).
       */
      const carried = defaultWithin(from);
      const then: BranchExpression | undefined =
        literal !== undefined
          ? { type: 'static', value: literal }
          : path !== undefined
            ? { type: 'calculated', path, ...(carried !== undefined ? { default: carried } : {}) }
            : undefined;
      const base = defaultConditionalExpression();
      return withNote({ ...base, then: then ?? base.then });
    }
    case 'suppress':
      /**
       * **The outcome has no value, and that is not a licence to destroy one** (`#1274`).
       *
       * This returned a bare `defaultSuppressExpression()` on the reasoning that *this field does
       * not go out* has nothing to hold. True of the **outcome**, and the mistake is that the shape
       * is also the only record of what the branch said a moment ago — so the identical control on a
       * mapping row, where omission is a slot beside the value, was reversible and this was not.
       *
       * `replaced` is where it goes. `from` rather than `current` so suppressing twice cannot build
       * a chain; the early return above has already handled `suppress → suppress`.
       */
      return withNote({
        ...defaultSuppressExpression(),
        replaced: from as Exclude<MacroExpression, SuppressExpression>,
      });
    /**
     * **Reshaping *to* a reference is not a reshape** (`XS5`, `#1066`).
     *
     * Every other arm carries what both shapes can hold. A reference holds no value of its own — it
     * holds an **address**, and there is no address derivable from a path or a literal. So this arm
     * is unreachable by design: a reference arrives from the `Shared…` picker, which is the only
     * thing that knows which expression is meant, and it is left by clearing the chip.
     *
     * Returning the value unchanged rather than throwing is the honest answer for an act nobody can
     * ask for through a control: a no-op the operator cannot reach beats an error they cannot cause.
     */
    case 'reference':
      return from;
    /**
     * **Nor is reshaping *to* a macro** (`X58`). A macro is typed, never reached by a chip, so there is
     * no shape to carry into; the value comes back as it was.
     */
    case 'macro':
      return from;
    /**
     * **Nor is reshaping *to* a tested value** (`X55b`). It is an address into the case that tested
     * it, and only the `Tested value…` chip knows which case is meant — the reference's argument.
     */
    case 'bound':
      return from;
  }
}

/**
 * **Stop suppressing — the value comes back as the shape it was** (`#1274`).
 *
 * The return leg, and it needs a name because the obvious spelling is wrong. A branch's wire answer
 * has two options, so *not suppressed* was written as `reshapeExpression(expr, 'static')` — the
 * right call when the operator is answering for the first time, and the wrong one the moment there
 * is something to give back: a suppression standing over a path reshapes to an **empty literal**,
 * because a path is not a literal and {@link reshapeExpression} correctly refuses to write one into
 * the other.
 *
 * So the carry `#1274` put in the model would have been stored, correct, and never reached — *a
 * ruling can be right while the mechanism is broken*, the mechanism here being one argument at one
 * call site. **The shape to return to is the shape that was taken away, and only the suppression
 * knows it.**
 */
export function unsuppressed(expr: MacroExpression | undefined): MacroExpression {
  if (expr?.type === 'suppress' && expr.replaced) return reshapeExpression(expr, expr.replaced.type);
  // Nothing was taken away — this branch has never been answered, or it was suppressed before
  // `#1274` and has nothing to give back. A literal is the shape an operator types into.
  return reshapeExpression(expr ?? defaultStaticExpression(), 'static');
}

/**
 * The one path this expression reads, where it reads exactly one.
 *
 * A **group** operand reads no single path and is not counted as one — collapsing
 * `x − (y × z)` to a path has no *one* answer any more than two flat paths do, and the rule there
 * is the same: carry nothing rather than let the first one silently win.
 */
/**
 * **An operand nobody has filled in is not an answer** (`X53`, `#1284`).
 *
 * *No single answer, so carry nothing* is the right rule and its subject is **authored** values: two
 * paths collapsing to one path is a guess made with the operator's own data. An empty row is the box
 * waiting to be typed into, and counting it as a second answer turns *there is nothing to choose
 * between* into *there is too much*, which loses the one value that was actually there.
 *
 * Reachable before this slice and in one more gesture — `Calculate…`, `+ Add Operand`, clear the
 * chip, and the path is gone — which is why it is fixed here rather than treated as padding's
 * consequence. `openingOperands` opening a group with two only made it the **first** thing an
 * operator would do.
 */
const operandIsEmpty = (operand: ValueOperand): boolean =>
  (operand.type === 'calculated' && operand.path.trim() === '') ||
  (operand.type === 'static' && String(operand.value).trim() === '');

/** The operands that hold something — the set *carry nothing where there are two* is asked of. */
const authoredOperands = (expr: ComputedExpression): ValueOperand[] =>
  readComputedOperands(expr).filter(
    // A list is many values, so it is never *the one path* or *the one literal* a reshape carries —
    // leaving a calculation over `[*]` drops it rather than planting `x[*].y` where it cannot resolve.
    (o): o is ValueOperand => o.type !== 'collection' && !operandIsEmpty(o),
  );

function pathWithin(expr: MacroExpression): string | undefined {
  if (expr.type === 'calculated') return expr.path;
  // A macro carries as the path it reaches (`X27`), where its concept declares one.
  if (expr.type === 'macro') return macroPath(expr.name) ?? undefined;
  if (expr.type === 'computed') {
    const paths = authoredOperands(expr).filter((o) => o.type === 'calculated');
    // More than one and there is no single answer, so nothing is carried rather than the first one
    // silently winning — a guess here is the *reads as proof* defect with the operator's own data.
    return paths.length === 1 ? paths[0].path : undefined;
  }
  if (expr.type === 'conditional') return expr.then.type === 'calculated' ? expr.then.path : undefined;
  return undefined;
}

/** The one literal this expression holds, where it holds exactly one. */
function literalWithin(expr: MacroExpression): string | undefined {
  if (expr.type === 'static') return expr.value;
  if (expr.type === 'computed') {
    const literals = authoredOperands(expr).filter((o) => o.type === 'static');
    return literals.length === 1 ? literals[0].value : undefined;
  }
  if (expr.type === 'conditional') return expr.then.type === 'static' ? expr.then.value : undefined;
  return undefined;
}

/**
 * **The default in force — for the two shapes that declare one, and the one that transports it.**
 *
 * A `conditional` declares no default of its own and should not: both branches always produce
 * something, or one of them suppresses. But since `#1241` it **carries** one, on whichever branch
 * holds the path, and the return leg has to know that or the round trip is still lossy — which is
 * the whole of `#1241`. Reading `then` here is what makes *path → `If…` → back* keep the value, and
 * it is the same asymmetry `pathWithin` already has.
 */
function defaultWithin(expr: MacroExpression): string | undefined {
  if (expr.type === 'calculated' || expr.type === 'computed' || expr.type === 'macro') return expressionDefaultOf(expr);
  if (expr.type === 'conditional' && expr.then.type === 'calculated') return expressionDefaultOf(expr.then);
  return undefined;
}

/**
 * **The simplest shape that still holds this value** — what clearing an errand returns to.
 *
 * `If…` and `Calculate…` put a value into a bigger shape; clearing one has to put it back, and *back*
 * is a path if the value reads one and a literal if it is one. Without this the only route out of a
 * conditional was the editor's type dropdown, which is the control `X39` retires — Todd, on slice 4:
 * *"once a chip is clicked there's no way to change it except the drop down, which I don't think
 * should be there. Given the functionality exists with the picker, the chips should be able to do the
 * same."*
 */
export function simplestShapeFor(expr: MacroExpression): 'calculated' | 'static' {
  const reshaped = reshapeExpression(expr, 'calculated');
  if (reshaped.type === 'calculated' && reshaped.path) return 'calculated';
  const asStatic = reshapeExpression(expr, 'static');
  return asStatic.type === 'static' && asStatic.value ? 'static' : 'calculated';
}

/**
 * **What the capability chips can do to *an expression*, and why not where they cannot.**
 *
 * The leaf adds two shapes an expression never has — a `{macro}` and a structured static — so
 * `MappingControl` answers those itself and defers here for the rest. One home for the wording, so a
 * chip refused on the row and the same chip refused inside the editor cannot come to say different
 * things (`#1187`: a duplicated explanation yields a wrong sentence nothing checks).
 */
export interface ChipCapabilities {
  /** The path Extract and Transform compose onto, or `null`. */
  basePath: string | null;
  /** Why Extract and Transform cannot apply, or `null`. */
  refusesComposition: { reason: string } | null;
}

/**
 * **The address a picked library entry becomes** (`X5`, `XS5`, `#1066`).
 *
 * One line, and it is a named function rather than an object literal inside the picker's `onClick`
 * for one reason: this console's test harness renders to static markup and cannot click, so an
 * address built inside a handler is assertable **nowhere**. The property worth pinning is that
 * picking stores an address and not a very short copy, and it needs somewhere to be pinned.
 */
export function referenceTo(entry: { id: string } & LibraryVersion): ReferenceExpression {
  const seenAt = entryVersion(entry);
  return { type: 'reference', ref: entry.id, ...(seenAt ? { seenAt } : {}) };
}

/**
 * **Promoting a value to the library leaves this row addressing it, not holding a copy of it**
 * (`X30d`, `#1304`, reported by Todd 2026-09-10: *"saving as shared expression doesn't replace local
 * copy with the reference."*).
 *
 * It is `X5` — *addressed, not copied* — failing at the one moment the copy is created. Pressing
 * *Save as shared* produced a library entry **and** left the row's own copy in place, so the two
 * were identical for exactly as long as it took somebody to edit either, and nothing on either
 * screen said they had ever been the same thing.
 *
 * **The note travels and nothing else does.** A note is about the mapping rather than about the
 * shape it currently has — the rule `reshapeExpression` and {@link copyOfReference} both keep — and
 * the *entry's* description is a different sentence, written in the save panel to describe the
 * shared thing. Everything else is now the library's, which is the point of promoting it.
 *
 * The version is recorded in the same act (`#1302`), so a row does not mark itself `changed` against
 * an entry it created a second earlier.
 */
export function referenceReplacing(
  expression: MacroExpression,
  entry: { id: string } & LibraryVersion,
): ReferenceExpression {
  const note = expression.description;
  return { ...referenceTo(entry), ...(note ? { description: note } : {}) };
}

/**
 * **What an edit to a reference's Default means, including an empty one** (`X36`, reported by Todd
 * 2026-09-09: *"backspacing … it keeps reverting their change when they're trying to delete the
 * whole number"*).
 *
 * The Default control emits `undefined` for an empty box, and on every other shape that is right —
 * empty means *no default*. On a reference it meant *no override*, so the value prop flipped to the
 * **inherited** layer on the keystroke that emptied the box, the control re-synced its draft from
 * it, and the operator's deletion was undone as they typed. `NumberField`'s family, arriving through
 * a value that has two layers rather than through a parse.
 *
 * So **an emptied box is an authored empty override**, and the way back to the inherited value is
 * the control that says so — `revert`, which exists precisely because these are two acts. An
 * operator clearing the box sees `overridden here` and the way back beside it, which is the honest
 * reading: they cleared it, and they can still take it back.
 */
export function referenceDefaultFrom(expression: ReferenceExpression, next: string | undefined): ReferenceExpression {
  return { ...expression, default: next ?? '' };
}

/**
 * **Revert an override by removing it, never by writing the inherited value in** (`X36`, `X31`).
 *
 * The two are indistinguishable on screen and opposite in effect: writing the value in freezes it,
 * so a later change in the library stops reaching this row — *addressed, not copied* undone one
 * layer down, silently. Named for the same reason as {@link referenceTo}: a behaviour built inside
 * a handler is assertable nowhere in a harness that cannot click.
 */
export function withoutReferenceOverride(expression: ReferenceExpression): ReferenceExpression {
  const { default: _override, ...rest } = expression;
  return rest;
}

export function chipCapabilitiesFor(expr: MacroExpression | undefined): ChipCapabilities {
  const basePath =
    expr?.type === 'calculated' ? expr.path || null : expr?.type === 'macro' ? macroPath(expr.name) : null;
  if (basePath) return { basePath, refusesComposition: null };
  if (expr?.type === 'macro') {
    return {
      basePath: null,
      refusesComposition: {
        reason: 'Extract and Transform act on a value read from the session, and this macro declares no path to read.',
      },
    };
  }

  if (!expr || expr.type === 'calculated') {
    return {
      basePath: null,
      refusesComposition: {
        reason: 'Extract and Transform act on a value read from the session, and this reads no path yet.',
      },
    };
  }
  if (expr.type === 'static') {
    return {
      basePath: null,
      refusesComposition: {
        reason:
          'Extract and Transform act on a value read from the session. This is a fixed value, so there is nothing to act on.',
      },
    };
  }
  if (expr.type === 'bound') {
    return {
      basePath: null,
      refusesComposition: {
        reason:
          'This uses the value an earlier case tested. Add an extraction or a transform to what that case tests, and every place reusing it sees the result.',
      },
    };
  }
  if (expr.type === 'reference') {
    // **The refusal names where the act belongs, and the redirect is one an operator can follow** —
    // the library page is where a shared expression is edited, and editing it here would either
    // change every row that uses it or fork a copy. Both are decisions, and neither is a chip.
    return {
      basePath: null,
      refusesComposition: {
        reason:
          'This row uses a shared expression. Add an extraction or a transform to it in the expression library, where the change reaches every row using it.',
      },
    };
  }
  return {
    basePath: null,
    refusesComposition: {
      reason:
        'Extract and Transform act on the value a path resolves to. Apply them inside this expression — each branch and each operand carries its own.',
    },
  };
}

/**
 * **Editing a copy detaches this row from the library, and carries what the row itself authored**
 * (`X30a`, ratified by Todd 2026-09-10 — *"the user is offered 'Edit a copy' or 'Edit the shared
 * expression'"*).
 *
 * It lives here rather than in the control that offers the choice, for `#1223`'s reason: **what
 * survives a change of shape is a property of the model, not of the surface offering the switch.**
 * A carry written inside a panel is discarded with the panel, and this one has to outlive the panel
 * by design — the same act reaches the row from a chip today and will reach it from the library
 * page's *Use here* tomorrow.
 *
 * Two things the row owns and the library entry does not, so both travel:
 *
 * - **The note.** A note is about the mapping, not about the shape it currently has — the rule
 *   `reshapeExpression` already keeps. The row's own note wins over the entry's description, which
 *   is a *different* sentence: one describes this use, the other describes the shared thing.
 * - **The local default override** (`X36`), **where the copied shape declares a default**. It is
 *   the same clause `reshapeExpression` applies, and for the same reason: a shape with no default
 *   slot cannot be handed one, and inventing a place to put it would be a second answer to *what is
 *   sent when this resolves to nothing* on a shape that already has one.
 *
 * An override on a shape that declares none is dropped, and that is the copy's cost rather than a
 * defect — the panel says so before the operator chooses, because *never suggest an action without
 * its consequence*.
 */
export function copyOfReference(reference: ReferenceExpression, shared: MacroExpression): MacroExpression {
  const note = reference.description ?? shared.description;
  const withNote = <T extends MacroExpression>(expr: T): T => (note ? { ...expr, description: note } : expr);
  const override = reference.default;
  if (override === undefined) return withNote({ ...shared });
  if (shared.type === 'calculated' || shared.type === 'computed' || shared.type === 'macro')
    return withNote({ ...shared, default: override });
  if (shared.type === 'conditional' && shared.then.type === 'calculated')
    return withNote({ ...shared, then: { ...shared.then, default: override } });
  return withNote({ ...shared });
}

/**
 * **Whether the copy can carry the row's local default** — the sentence the choice needs, derived
 * from the same clause that performs the carry rather than restated beside it (`#1269`: a rule
 * written as a condition in two places is a rule with two implementations).
 */
export function copyWouldDropTheOverride(reference: ReferenceExpression, shared: MacroExpression): boolean {
  if (reference.default === undefined) return false;
  const copy = copyOfReference(reference, shared);
  return !copyCarriesDefault(copy, reference.default);
}

const copyCarriesDefault = (copy: MacroExpression, override: string): boolean => {
  if (copy.type === 'calculated' || copy.type === 'computed' || copy.type === 'macro')
    return expressionDefaultOf(copy) === override;
  if (copy.type === 'conditional' && copy.then.type === 'calculated')
    return expressionDefaultOf(copy.then) === override;
  return false;
};
